import React, { useState } from 'react';
import { Modal, Input, Tabs } from 'antd';
import { Search } from 'lucide-react';
import type { InterviewTemplate } from '@/services/api';
import { Z_INDEX_MODAL } from '@/styles';
import { partsOf, templateStatus, type AssessmentsByTemplate, type TemplatePart } from './interviewStatus';
import styles from './InterviewPickerModal.module.scss';

interface InterviewPickerModalProps {
  open: boolean;
  onClose: () => void;
  templates: InterviewTemplate[];
  assessmentsByTemplate: AssessmentsByTemplate;
  onPick: (templateId: string) => void;
}

const partTagText = (part: TemplatePart): string => {
  if (part.status === 'done') return `${part.short} ✓`;
  if (part.status === 'prog') return `${part.short} · In progress`;
  return part.short;
};

export const InterviewPickerModal: React.FC<InterviewPickerModalProps> = ({
  open,
  onClose,
  templates,
  assessmentsByTemplate,
  onPick,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'prog' | 'done' | 'new'>('all');

  const close = () => {
    onClose();
    setSearch('');
    setFilter('all');
  };

  const trimmedSearch = search.trim();
  const filtered = templates.filter((t) => {
    if (trimmedSearch) return t.name.toLowerCase().includes(trimmedSearch.toLowerCase());
    if (filter !== 'all' && templateStatus(t, assessmentsByTemplate) !== filter) return false;
    return true;
  });

  const countOf = (status: 'prog' | 'done' | 'new') =>
    templates.filter((t) => templateStatus(t, assessmentsByTemplate) === status).length;

  return (
    <Modal
      open={open}
      onCancel={close}
      width="min(640px, calc(100vw - 28px))"
      zIndex={Number(Z_INDEX_MODAL)}
      rootClassName={styles.interviewModal}
      title={
        <div className={styles.interviewModalHead}>
          <span className={styles.interviewModalHeadTitle}>Select interview</span>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by interview name"
            prefix={<Search className={styles.interviewSearchIcon} style={{ width: '1em', height: '1em' }} />}
            allowClear
            className={styles.interviewSearch}
          />
          <Tabs
            activeKey={filter}
            onChange={(key) => setFilter(key as 'all' | 'prog' | 'done' | 'new')}
            className={styles.filterTabs}
            items={[
              { key: 'all', label: `All ${templates.length}` },
              { key: 'prog', label: `In progress ${countOf('prog')}` },
              { key: 'done', label: `Completed ${countOf('done')}` },
              { key: 'new', label: `Not started ${countOf('new')}` },
            ]}
          />
        </div>
      }
      footer={
        <div className={styles.interviewModalFooter}>
          <span className={styles.interviewModalFooterNote}>
            {filtered.length} of {templates.length}
          </span>
        </div>
      }
    >
      <div className={styles.interviewOptionList}>
        {filtered.length === 0 ? (
          <div className={styles.interviewEmpty}>No interviews match &ldquo;{search}&rdquo;.</div>
        ) : (
          filtered.map((t) => {
            const status = templateStatus(t, assessmentsByTemplate);
            const parts = partsOf(t, assessmentsByTemplate);
            return (
              <button
                key={t.id}
                type="button"
                className={styles.interviewOption}
                onClick={() => {
                  onPick(t.id);
                  close();
                }}
              >
                <span className={styles.interviewOptionTop}>
                  <span className={styles.interviewOptionName}>{t.name}</span>
                  <span
                    className={`${styles.interviewOptionStatus} ${
                      status === 'done'
                        ? styles.interviewOptionStatusDone
                        : status === 'prog'
                          ? styles.interviewOptionStatusProg
                          : ''
                    }`}
                  >
                    {status === 'done' ? 'Completed' : status === 'prog' ? 'In progress' : 'Not started'}
                  </span>
                </span>
                <span className={styles.interviewOptionTags}>
                  {parts.map((p) => (
                    <span
                      key={p.key}
                      className={`${styles.interviewOptionTag} ${
                        p.key === 'hts' ? styles.interviewOptionTagHts : styles.interviewOptionTagComp
                      }`}
                    >
                      {partTagText(p)}
                    </span>
                  ))}
                </span>
              </button>
            );
          })
        )}
      </div>
    </Modal>
  );
};

export default InterviewPickerModal;
